import { BASECAMP_PROJECT_TODO_IN_PATH_RE } from '@/lib/basecamp-url';
import type { BasecampAssignment } from '@/types/basecamp';

export interface ParsedBasecampSearchQuery {
  todoId?: string;
  projectId?: string;
}

/** Parse bare ID, Basecamp URL, or FlowNotes project/todo route from search box input. */
export function parseBasecampSearchQuery(query: string): ParsedBasecampSearchQuery {
  const trimmed = query.trim();
  if (!trimmed) return {};

  if (/^\d{10,}$/.test(trimmed)) {
    return { todoId: trimmed };
  }

  const bcMatch = trimmed.match(/\/(?:buckets|projects)\/(\d+)\/todos\/(\d+)/i);
  if (bcMatch) {
    return { projectId: bcMatch[1], todoId: bcMatch[2] };
  }

  const fnMatch = trimmed.match(BASECAMP_PROJECT_TODO_IN_PATH_RE);
  if (fnMatch) {
    return { projectId: fnMatch[1], todoId: fnMatch[2] };
  }

  return {};
}

export function assignmentMatchesBasecampQuery(
  assignment: BasecampAssignment,
  query: string,
): boolean {
  const q = query.trim();
  if (!q) return true;

  const parsed = parseBasecampSearchQuery(q);
  if (parsed.todoId) {
    const id = parsed.todoId;
    const matchesId =
      String(assignment.id) === id ||
      (assignment._parent_todo_id != null && String(assignment._parent_todo_id) === id);
    if (!matchesId) return false;
    if (parsed.projectId) {
      const projectId = assignment.project?.id ?? assignment._basecamp_project_id;
      return String(projectId) === String(parsed.projectId);
    }
    return true;
  }

  const lower = q.toLowerCase();
  return (
    assignment.title.toLowerCase().includes(lower) ||
    (assignment._project_name ?? assignment.project?.name ?? '').toLowerCase().includes(lower) ||
    (assignment._parent_title ?? '').toLowerCase().includes(lower) ||
    String(assignment.id).includes(q)
  );
}

export type AssignmentDisplayRow = BasecampAssignment & {
  _indent?: boolean;
  _isSubtaskRow?: boolean;
};

/** Group assigned subtasks under parent rows when both appear in the filtered set. */
export function buildAssignmentDisplayRows(
  list: BasecampAssignment[],
): AssignmentDisplayRow[] {
  const parentIds = new Set(
    list
      .filter((a) => a._recording_type !== 'Kanban::Step')
      .map((a) => String(a.id)),
  );

  const parents = list.filter((a) => a._recording_type !== 'Kanban::Step');
  const steps = list.filter((a) => a._recording_type === 'Kanban::Step');

  const nestedByParent = new Map<string, BasecampAssignment[]>();
  const orphans: BasecampAssignment[] = [];

  for (const step of steps) {
    const pid = step._parent_todo_id;
    if (pid && parentIds.has(String(pid))) {
      const key = String(pid);
      if (!nestedByParent.has(key)) nestedByParent.set(key, []);
      nestedByParent.get(key)!.push(step);
    } else {
      orphans.push(step);
    }
  }

  const rows: AssignmentDisplayRow[] = [];
  for (const parent of parents) {
    rows.push(parent);
    for (const child of nestedByParent.get(String(parent.id)) ?? []) {
      rows.push({ ...child, _indent: true, _isSubtaskRow: true });
    }
  }
  for (const orphan of orphans) {
    rows.push({ ...orphan, _isSubtaskRow: true });
  }
  return rows;
}
